import React from 'react';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Dialog = styled.div`
  background: var(--color-white);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-xl);
  min-width: 400px;
  max-width: 500px;
`;

const Header = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid var(--color-gray-200);
`;

const Title = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-gray-900);
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Content = styled.div`
  padding: 1.5rem;
`;

const Message = styled.p`
  margin: 0;
  color: var(--color-gray-700);
  line-height: 1.5;
`;

const Actions = styled.div`
  padding: 1.5rem;
  border-top: 1px solid var(--color-gray-200);
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
`;

const Button = styled.button<{ $variant?: 'primary' | 'danger' | 'secondary' }>`
  padding: 0.5rem 1rem;
  border-radius: var(--border-radius);
  font-weight: 500;
  border: 1px solid;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => {
    switch (props.$variant) {
      case 'danger':
        return `
          background: var(--color-red-600);
          border-color: var(--color-red-600);
          color: white;
          &:hover {
            background: var(--color-red-700);
            border-color: var(--color-red-700);
          }
        `;
      case 'primary':
        return `
          background: var(--color-primary);
          border-color: var(--color-primary);
          color: white;
          &:hover {
            background: var(--color-primary-dark);
            border-color: var(--color-primary-dark);
          }
        `;
      default:
        return `
          background: white;
          border-color: var(--color-gray-300);
          color: var(--color-gray-700);
          &:hover {
            background: var(--color-gray-50);
            border-color: var(--color-gray-400);
          }
        `;
    }
  }}
`;

export interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'primary';
  icon?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'primary',
  icon,
  onConfirm,
  onCancel
}) => {
  if (!isOpen) return null;

  return (
    <Overlay onClick={onCancel}>
      <Dialog onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>
            {icon && <span>{icon}</span>}
            {title}
          </Title>
        </Header>
        <Content>
          <Message>{message}</Message>
        </Content>
        <Actions>
          <Button onClick={onCancel}>
            {cancelText}
          </Button>
          <Button $variant={variant} onClick={onConfirm}>
            {confirmText}
          </Button>
        </Actions>
      </Dialog>
    </Overlay>
  );
};